import { ITokenSuggestion } from '../Storefront/useOpportunityTokens';
import { TokenContext } from '../../models/Storefront';
import { useTokenSuggestions } from '../Common/useTokenSuggestions';
import {
    WEBHOOK_TYPE_EXTENSION_ERROR, WEBHOOK_TYPE_SF_CART_COMMENT, WEBHOOK_TYPE_SF_ORDER,
    WEBHOOK_TYPE_SF_QUOTE_REQUEST, WEBHOOK_TYPE_SF_SIGNUP, WEBHOOK_TYPE_SF_SIGNUP_APPROVAL,
} from '../../models/Webhooks';

// Which tokens a webhook's custom request body can actually use, by webhook type.
//
// This is a per-type answer, not one list: each event fires from its own code path with its own token
// dictionary, so a token that renders on a quote request is left as a literal {{{token}}} in a signup
// payload. The mapping mirrors the dispatch sites:
//   sf_order                         -> StorefrontOrderClient.GetTokens
//   sf_quote_request, sf_cart_comment -> PreparedOrderDeliver.GetNotificationTokens
//   sf_signup, sf_signup_approval    -> StorefrontNotificationClient.BuildSignupTokens
//   extension_error                  -> ExtensionActivityLogger.BuildTokens
//
// The lists themselves come from the API (the same catalog endpoint the opportunity override fields
// use) rather than being restated here, so they cannot drift from the server-side builders.
//
// Types absent from this map get no autosuggest, which is the honest answer rather than a guess:
//   * The procurement types don't render a custom body at all -- RunProcurementWebhooks never passes
//     one to the dispatcher, so whatever is typed there is ignored and the default payload is sent.
//   * sf_abandoned_cart uses a different, two-token {{...}} syntax.
//   * dpl_export has no token pass of its own.
const CONTEXT_BY_TYPE: Record<string, TokenContext> = {
    [WEBHOOK_TYPE_SF_ORDER]: 'order',
    [WEBHOOK_TYPE_SF_QUOTE_REQUEST]: 'quoteRequest',
    [WEBHOOK_TYPE_SF_CART_COMMENT]: 'quoteRequest',
    [WEBHOOK_TYPE_SF_SIGNUP]: 'signup',
    [WEBHOOK_TYPE_SF_SIGNUP_APPROVAL]: 'signup',
    [WEBHOOK_TYPE_EXTENSION_ERROR]: 'extensionError',
};

/** True when a custom request body on this webhook type is sent verbatim, tokens un-rendered. */
export function bodyIgnoresTokens(type?: string): boolean {
    return !!type && !CONTEXT_BY_TYPE[type];
}

export interface IWebhookTokens {
    suggestions: ITokenSuggestion[];
    loading: boolean;
    /** The catalog context this type reads, or undefined when it renders no tokens. */
    context?: TokenContext;
    /** False when this webhook type renders no tokens, so the caller can fall back to a plain field. */
    supported: boolean;
}

export function useWebhookTokens(type?: string): IWebhookTokens {
    const context = type ? CONTEXT_BY_TYPE[type] : undefined;
    // Shared RTK Query cache with every other token consumer, so switching between webhook types of
    // the same context costs nothing.
    const { suggestions, loading } = useTokenSuggestions(context);
    return { suggestions, loading, supported: !!context, context };
}
